import {
    ApiBonusAccountState,
    ApiPromoCodeStatus,
} from '@/core/generated/api';
import type { ActionMenuItem } from '@/shared/ui/ActionMenu/types';
import { getOrderActionState } from './orderActionState';
import type { RecordActionCommands, RecordActionContext, RecordActionKey } from './types';

/** Собирает действия записи с условиями её карточки */
export const getRecordActionItems = (
    context: RecordActionContext,
    commands: RecordActionCommands,
    pending = false,
): ActionMenuItem[] => {
    /** Описывает доступное действие записи */
    const item = (
        key: RecordActionKey,
        label: string,
        permission: string,
        icon: string,
        visible = true,
        disabled = false,
    ): ActionMenuItem => ({
        id: key,
        label,
        permission,
        icon: `pi pi-${icon}`,
        visible: visible && Boolean(commands[key]),
        disabled: pending || disabled,
        className: key === 'block' || key === 'blockNew' || key === 'deactivate' || key === 'cancel'
            ? 'p-menuitem-danger'
            : undefined,
        command: () => commands[key]?.(),
    });

    /** Описывает переход к редактированию */
    const edit = (permission: string, visible = true) => item('edit', 'Редактировать', permission, 'pencil', visible);

    switch (context.kind) {
        case 'clients':
        case 'executors': {
            const executor = context.kind === 'executors';
            const profile = context.record;

            const scope = executor
                ? 'executors'
                : 'clients';

            const blocked = profile.isBlocked === true;
            const result: ActionMenuItem[] = [];

            if (context.kind === 'clients') {
                result.push(item('createOrder', 'Сформировать заказ', 'admin.orders.create', 'plus'));
            }

            if (context.kind === 'executors') {
                result.push(
                    item('verify', 'Верифицировать', 'admin.executors.verify', 'check-circle', !context.record.isVerified),
                    item('assignPenalty', 'Назначить штраф', 'admin.executors.penalty.assign', 'exclamation-triangle', !context.record.hasPenalty),
                    item('removePenalty', 'Снять штраф', 'admin.executors.penalty.remove', 'check-circle', context.record.hasPenalty === true),
                );
            }

            result.push(
                item('chat', executor
                    ? 'Чат с исполнителем'
                    : 'Чат с клиентом', 'admin.chats.view', 'comments'),
                item('email', 'Привязать почту', `admin.${scope}.email.bind`, 'envelope', true, !profile.appUserId),
                item('transferPhone', 'Перенести профиль', `admin.${scope}.phone.transfer`, 'phone', true, !profile.appUserId),
            );

            if (context.kind === 'executors') {
                result.push(item('passport', 'Указать паспортные данные', 'admin.executors.passport.register', 'id-card', context.record.rocketWorkId == null));
            }

            result.push(
                edit(`admin.${scope}.edit`),
                item(blocked
                    ? 'unblock'
                    : 'block', blocked
                    ? 'Разблокировать'
                    : 'Заблокировать',
                    `admin.${scope}.${blocked
                        ? 'unblock'
                        : 'block'}`, blocked
                    ? 'lock-open'
                    : 'lock'),
                item('deactivate', 'Деактивировать', `admin.${scope}.deactivate`, 'trash'),
            );

            return result;
        }
        case 'promoCodes': {
            const isNew = context.record.status?.id === ApiPromoCodeStatus.New;
            const isActive = context.record.status?.id === ApiPromoCodeStatus.Active;

            return [
                edit('admin.promo-codes.edit', isNew && (!context.record.printingId || Boolean(context.record.isStandalone))),
                item('activate', 'Активировать', 'admin.promo-codes.activate', 'check-circle', isNew),
                item('block', 'Заблокировать', 'admin.promo-codes.block', 'ban', isNew || isActive),
            ];
        }
        case 'promoCodePrintings': {
            const issued = Boolean(context.record.issuedAt);
            const hasNew = (context.record.newCount ?? 0) > 0;

            return [
                item('generate', 'Выпустить промокоды', 'admin.promo-code-printings.issue', 'ticket', !issued),
                item('import', 'Загрузить промокоды из Excel', 'admin.promo-code-printings.issue', 'upload', !issued),
                item('export', 'Выгрузить в Excel', 'admin.promo-code-printings.view', 'file-excel', true, !issued),
                item('activate', 'Активировать промокоды', 'admin.promo-code-printings.activate', 'check', true, !hasNew),
                { separator: true },
                item('block', 'Заблокировать тираж', 'admin.promo-code-printings.block', 'ban', true, !issued),
                item('blockNew', 'Заблокировать невыданные', 'admin.promo-code-printings.block-new', 'ban', true, !hasNew),
            ];
        }
        case 'bonusAccounts': {
            const blocked = context.record.state?.id === ApiBonusAccountState.Blocked;

            return [
                item('accrue', 'Начислить бонусы', 'admin.bonus-accounts.accrue', 'plus', !blocked),
                item('spend', 'Списать бонусы', 'admin.bonus-accounts.spend', 'minus', !blocked),
                item('block', 'Заблокировать счёт', 'admin.bonus-accounts.block', 'ban', !blocked),
                item('unblock', 'Разблокировать счёт', 'admin.bonus-accounts.unblock', 'check-circle', blocked),
            ];
        }
        case 'orders': {
            const state = getOrderActionState(context.record, context.task);

            return [
                edit('admin.orders.edit'),
                item('startExecution', 'Начать выполнение', 'admin.orders.start', 'play', state.isExecutorAssigned && Boolean(context.record.executorId)),
                item('complete', 'Завершить заказ', 'admin.orders.complete', 'check-circle', state.isInProgress),
                item('confirmPayment', 'Подтвердить оплату', 'admin.orders.payments.confirm', 'dollar', state.shouldShowManualPaymentConfirmButton),
                item('startSearch', 'Начать поиск исполнителя', 'admin.orders.schedule', 'search', state.shouldShowStartExecutorSearchButton),
                item('approveExecutor', 'Утвердить исполнителя', 'admin.orders.approve-executor', 'check-circle', state.isRequestPending),
                item('removeExecutor', 'Снять исполнителя с заказа', 'admin.orders.remove-executor', 'user-minus', state.canRemoveExecutor),
                item('payTask', 'Направить оплату', 'admin.orders.tasks.pay', 'credit-card', state.shouldShowPayForTaskButton),
                item('assignExecutor', 'Назначить исполнителя', 'admin.orders.assign-executor', 'user-plus', state.isSearchable),
                item('createPayment', 'Сформировать ссылку на оплату', 'admin.orders.payments.create', 'credit-card', state.shouldShowCreatePaymentButton),
                item('repeat', 'Повторить заказ', 'admin.orders.repeat', 'copy'),
                item('createTask', 'Создать задачу', 'admin.operator-tasks.create', 'plus', context.canCreateTask),
                item('cancel', 'Отменить заказ', 'admin.orders.cancel', 'times', state.shouldShowCancelButton),
            ];
        }
        case 'orderProblems':
            return [
                item('retry', 'Повторить обработку', 'admin.order-problems.retry', 'refresh', context.record.canRetry === true),
                item('resolve', 'Отметить решённой', 'admin.order-problems.resolve', 'check', context.record.canResolve === true),
            ];
        case 'operatorTasks': {
            const completed = context.record.status?.id === 'completed';

            return [
                edit('admin.operator-tasks.edit', !completed),
                item(completed
                    ? 'reopen'
                    : 'complete', completed
                    ? 'Вернуть в работу'
                    : 'Выполнено',
                    completed
                        ? 'admin.operator-tasks.reopen'
                        : 'admin.operator-tasks.complete', completed
                    ? 'replay'
                    : 'check', true, !context.record.version),
            ];
        }
        case 'dialogBroadcasts':
            return [
                item('refresh', 'Обновить', 'admin.dialogs.view', 'refresh'),
                item('openTemplate', 'Открыть шаблон', 'admin.dialogs.view', 'file-edit'),
            ];
        case 'problemAddresses':
            return [item('verifyAddress', 'Проверить координаты', 'admin.problem-addresses.verify', 'map-marker')];
        default: {
            const scopes = {
                appVersions: 'app-versions',
                notificationTemplates: 'notification-templates',
                promotionBanners: 'promotion-banner-templates',
                subscriptions: 'subscriptions',
                clientSubscriptions: 'clients.subscriptions',
                pets: 'clients.pets',
                tags: 'tags',
                cashbackLevels: 'cashback-levels',
                messageTemplates: 'message-templates',
                scheduler: 'scheduler',
                dialogTemplates: 'dialogs',
                products: 'products',
                priceLists: 'price-lists',
                policies: 'policies',
                blogs: 'blogs',
            };

            const scope = scopes[context.kind];

            const permission = context.kind === 'messageTemplates'
                ? 'admin.message-templates.manage'
                : context.kind === 'dialogTemplates'
                    ? 'admin.dialogs.manage'
                    : `admin.${scope}.edit`;

            return [
                item('audit', 'История изменений', 'admin.products.audit', 'history', context.kind === 'products'),
                edit(permission),
                item('deactivate', 'Деактивировать', `admin.${scope}.deactivate`, context.kind === 'policies'
                    ? 'trash'
                    : 'ban'),
            ];
        }
    }
};
